
import { useState, useEffect } from "react";
import { getUserById, getTransactionsByUserId } from "@/lib/firebase";
import { User, Transaction } from "@/types";
import { useToast } from "@/hooks/use-toast";

export function useChildData(id: string | undefined) {
  const [child, setChild] = useState<User | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchTransactions = async () => {
    if (!id) return;
    try {
      console.log("Fetching transactions for user ID:", id);
      const transactionsData = await getTransactionsByUserId(id);
      console.log("Transactions fetched:", transactionsData);
      setTransactions(transactionsData as Transaction[]);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast({
        title: "Error",
        description: "Failed to load transactions. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchChildData = async () => {
    if (!id) {
      console.error("No ID provided in URL params");
      return;
    }
    
    setIsLoading(true);
    try {
      console.log("Fetching child data for ID:", id);
      const childData = await getUserById(id);
      console.log("Child data fetched:", childData);
      
      if (childData) {
        setChild(childData);
        document.title = `${childData.firstName}'s Account | Shekel`;
        await fetchTransactions();
      } else {
        console.error("No child data found for ID:", id);
      }
    } catch (error) {
      console.error("Error fetching child data:", error);
      toast({
        title: "Error",
        description: "Failed to load child data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshChildData = async () => {
    if (!id) return;
    const childData = await getUserById(id);
    setChild(childData);
  };

  useEffect(() => {
    fetchChildData();
  }, [id]);

  return {
    child,
    setChild,
    transactions,
    isLoading,
    fetchTransactions,
    refreshChildData
  };
}
